// Ordenação genérica de listagens por clique no cabeçalho (Ricardo, 18/10/2026):
// 1º clique = crescente ▲, 2º = decrescente ▼, 3º volta ao crescente. Ordena os DADOS (não o DOM).
// Tipos: número (inclusive string pt-BR "1.234,56"), data DD/MM/AAAA[ HH:MM[:SS]] e ISO, texto
// (localeCompare pt-BR sem diferenciar maiúsculas/acentos). Vazios sempre no fim.
import { useMemo, useRef, useState, type ReactNode, type ThHTMLAttributes } from "react";

export type Dir = "asc" | "desc";
export type Colunas<T> = Record<string, (linha: T) => unknown>;

const collator = new Intl.Collator("pt-BR", { sensitivity: "base", numeric: true });

const RE_NUM_BR = /^-?(\d{1,3}(\.\d{3})+|\d+)(,\d+)?$/;
const RE_HMS = /^(-?)(\d+):([0-5]\d)(?::([0-5]\d))?$/;
const RE_DUR = /^(?:(\d+)h)?\s*(?:(\d+)min)?\s*(?:(\d+)s)?$/;
// DD/MM/AAAA ou DD/MM/AA, com hora opcional ("01/08/26 às 10:44:23")
const RE_DATA_BR = /^(\d{2})\/(\d{2})\/(\d{4}|\d{2})(?:(?:\s+|T)(?:às\s+)?(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/;
const RE_DATA_ISO = /^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2}(\.\d+)?)?(Z|[+-]\d{2}:?\d{2})?)?$/;

function vazio(v: unknown): boolean {
  return v == null || (typeof v === "string" && (v.trim() === "" || v.trim() === "—"));
}

function comoNumero(v: unknown): number | null {
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (typeof v !== "string") return null;
  const s = v.trim().replace(/^R\$\s*/, "").replace(/\s*%$/, "");
  if (RE_NUM_BR.test(s)) return Number(s.replace(/\./g, "").replace(",", "."));
  const hm = RE_HMS.exec(s); // duração/hora "H:MM" ou "H:MM:SS" → segundos
  if (hm) return (hm[1] ? -1 : 1) * (+(hm[2] ?? 0) * 3600 + +(hm[3] ?? 0) * 60 + +(hm[4] ?? 0));
  const d = RE_DUR.exec(s); // "1h 05min", "2min 10s", "45s"
  if (d && (d[1] || d[2] || d[3])) return +(d[1] ?? 0) * 3600 + +(d[2] ?? 0) * 60 + +(d[3] ?? 0);
  return null;
}

function comoData(v: unknown): number | null {
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v.getTime();
  if (typeof v !== "string") return null;
  const s = v.trim();
  const m = RE_DATA_BR.exec(s);
  if (m) {
    const ano = +(m[3] ?? 0);
    return Date.UTC(ano < 100 ? 2000 + ano : ano, +(m[2] ?? 1) - 1, +(m[1] ?? 1), +(m[4] ?? 0), +(m[5] ?? 0), +(m[6] ?? 0));
  }
  if (RE_DATA_ISO.test(s)) {
    const t = Date.parse(s);
    return isNaN(t) ? null : t;
  }
  return null;
}

type Tipo = "num" | "data" | "texto";

function tipoDaColuna(valores: unknown[]): Tipo {
  const cheios = valores.filter((v) => !vazio(v));
  if (cheios.length === 0) return "texto";
  if (cheios.every((v) => comoData(v) != null)) return "data";
  if (cheios.every((v) => comoNumero(v) != null)) return "num";
  return "texto";
}

export function ordenarLinhas<T>(linhas: T[], acessor: (l: T) => unknown, dir: Dir): T[] {
  const valores = linhas.map(acessor);
  const tipo = tipoDaColuna(valores);
  const chave = (v: unknown): number | string =>
    tipo === "num" ? (comoNumero(v) as number) : tipo === "data" ? (comoData(v) as number) : String(v);
  const idx = linhas.map((_, i) => i);
  const sinal = dir === "asc" ? 1 : -1;
  idx.sort((a, b) => {
    const va = valores[a], vb = valores[b];
    const ea = vazio(va), eb = vazio(vb);
    if (ea || eb) return ea && eb ? a - b : ea ? 1 : -1; // vazios no fim, nos dois sentidos
    const ka = chave(va), kb = chave(vb);
    const c = typeof ka === "number" && typeof kb === "number" ? ka - kb : collator.compare(String(ka), String(kb));
    return c !== 0 ? c * sinal : a - b; // estável
  });
  return idx.map((i) => linhas[i] as T);
}

export interface Ordenacao<T> {
  linhas: T[];
  col: string | null;
  dir: Dir;
  alternar: (col: string) => void;
  limpar: () => void;
  /** índice da linha na lista ORIGINAL (para seleção/ações que indexam o array recebido) */
  idx: (linha: T) => number;
}

export function useOrdenacao<T>(linhas: T[] | null | undefined, colunas: Colunas<T>): Ordenacao<T> {
  const [estado, setEstado] = useState<{ col: string | null; dir: Dir }>({ col: null, dir: "asc" });
  const colsRef = useRef(colunas);
  colsRef.current = colunas;
  const ordenadas = useMemo(() => {
    const base = linhas ?? [];
    const acessor = estado.col ? colsRef.current[estado.col] : undefined;
    return acessor ? ordenarLinhas(base, acessor, estado.dir) : base;
  }, [linhas, estado]);
  const mapa = useMemo(() => new Map((linhas ?? []).map((l, i) => [l, i] as const)), [linhas]);
  return {
    linhas: ordenadas,
    col: estado.col,
    dir: estado.dir,
    alternar: (col) =>
      setEstado((s) => (s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" })),
    limpar: () => setEstado({ col: null, dir: "asc" }),
    idx: (l) => mapa.get(l) ?? -1,
  };
}

type ThProps = ThHTMLAttributes<HTMLTableCellElement> & {
  ord: { col: string | null; dir: Dir; alternar: (col: string) => void };
  col: string;
  children?: ReactNode;
};

// <th> clicável: seta ▲/▼ só na coluna ativa; ⇅ discreta nas demais ao passar o mouse (CSS .th-ord).
export function ThOrdena({ ord, col, children, className, onClick, ...rest }: ThProps) {
  const ativa = ord.col === col;
  return (
    <th
      {...rest}
      className={`th-ord${ativa ? " th-ord-ativa" : ""}${className ? " " + className : ""}`}
      aria-sort={ativa ? (ord.dir === "asc" ? "ascending" : "descending") : "none"}
      title={rest.title ?? "Clique para ordenar"}
      onClick={(e) => {
        ord.alternar(col);
        onClick?.(e);
      }}
    >
      {children}
      <span className="th-ord-seta" aria-hidden="true">
        {ativa ? (ord.dir === "asc" ? "▲" : "▼") : "⇅"}
      </span>
    </th>
  );
}
